import { z } from 'zod';
import { localeSchema } from './common';
import { moduleKeySchema } from './modules';
import type { PlatformPermission } from './platform';
import { projectSummarySchema } from './projects';
import { themeSchema } from './theme';

/**
 * The deployment: one installation of the product, stood up for one client.
 *
 * This is the record the provider console edits, and it is a real table rather
 * than columns bolted onto `organization` or a generic settings blob. The same
 * reasoning `settings.ts` gives for keeping the working calendar in its own
 * table applies with more force here: the deployment record decides what the
 * whole application *is*, so it wants its own `version` for optimistic
 * concurrency, its own validation, and its own audit trail. A row in a
 * key/value table cannot have any of those.
 *
 * It is one-to-one with the organization on purpose. The organization is who
 * the client is; the deployment is what we built for them. Merging the two
 * would put a provider-only field like the support contact in the payload
 * every client user already receives at boot.
 */

/**
 * Where a deployment is in its life.
 *
 * `provisioning` is the state a new installation is created in, and it is not
 * cosmetic: only platform accounts may sign in while it holds, so a provider
 * can theme the shell, pick the modules and load the client's project without
 * the client's own users walking into a half-built product. Handing over is an
 * explicit act -- the "Activate" button -- rather than something that happens
 * by accident the moment the first account is created.
 *
 * `suspended` is the same lock applied later, for a lapsed contract or an
 * incident. It is deliberately not a delete: nothing is destroyed, and turning
 * it back is one click.
 */
export const DEPLOYMENT_STAGES = ['provisioning', 'active', 'suspended'] as const;
export const deploymentStageSchema = z.enum(DEPLOYMENT_STAGES);
export type DeploymentStage = (typeof DEPLOYMENT_STAGES)[number];

/** Stages in which a tenant (non-platform) session is refused. */
export const CLOSED_DEPLOYMENT_STAGES: readonly DeploymentStage[] = ['provisioning', 'suspended'];

export function isDeploymentOpen(stage: DeploymentStage): boolean {
  return !CLOSED_DEPLOYMENT_STAGES.includes(stage);
}

/**
 * What the shell needs to know about its own deployment, as carried in the
 * session context.
 *
 * Thin on purpose, and the mirror of `projectSummarySchema`: the stage, so the
 * shell can render a maintenance notice instead of an empty product, and the
 * enabled sets, so the dashboard editor can drop a tile whose module was
 * switched off between the view being saved and being read. The provider's own
 * fields -- client name, support contact, notes -- are not here; they are
 * provider records, and every client user would otherwise receive them at boot.
 */
export const deploymentSummarySchema = z.object({
  stage: deploymentStageSchema,
  modules: z.array(moduleKeySchema),
  components: z.array(z.string()),
});
export type DeploymentSummary = z.infer<typeof deploymentSummarySchema>;

export const deploymentSchema = z.object({
  organizationId: z.string().uuid(),
  /** Immutable after creation: it is in URLs, log lines and the OIDC config. */
  slug: z.string(),
  /** The organization name, shown to the client's own users. */
  name: z.string(),
  defaultLocale: localeSchema,
  stage: deploymentStageSchema,
  modules: z.array(moduleKeySchema),
  components: z.array(z.string()),
  theme: themeSchema,
  /** Provider-facing only. Who this deployment is for, in the provider's words. */
  clientName: z.string().nullable(),
  /** Where the client's users are told to go when something breaks. */
  supportContact: z.string().nullable(),
  /** Provider notes: contract reference, hand-over date, whatever is useful. */
  notes: z.string().nullable(),
  /** The project the deployment is about, or null before one is provisioned. */
  project: projectSummarySchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Deployment = z.infer<typeof deploymentSchema>;

/**
 * Four update schemas rather than one, because they are governed by three
 * different permissions and one of them is a lifecycle transition.
 *
 * A single `PATCH /deployment` taking every field would mean the guard could
 * only require the union of the permissions -- and a support engineer allowed
 * to read a deployment and fix a typo in the support contact would need the
 * permission that lets them switch Costing off. Splitting the endpoint splits
 * the blast radius.
 */
export const updateDeploymentIdentitySchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    defaultLocale: localeSchema.optional(),
    clientName: z.string().max(200).nullish(),
    supportContact: z.string().max(200).nullish(),
    notes: z.string().max(4000).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateDeploymentIdentity = z.infer<typeof updateDeploymentIdentitySchema>;

export const updateDeploymentStageSchema = z
  .object({
    stage: deploymentStageSchema,
    /**
     * Required for anything but activation. Suspending a deployment locks the
     * client out of their own data, which is the kind of act that should leave
     * a sentence behind in the audit log explaining itself.
     */
    reason: z.string().max(500).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict()
  .refine((value) => value.stage !== 'suspended' || (value.reason?.trim().length ?? 0) > 0, {
    message: 'a reason is required when suspending a deployment',
    path: ['reason'],
  });
export type UpdateDeploymentStage = z.infer<typeof updateDeploymentStageSchema>;

export const updateDeploymentModulesSchema = z
  .object({
    modules: z.array(moduleKeySchema).max(32),
    /**
     * Component types offered in the dashboard editor. Clamped server-side to
     * the components the chosen modules bring, so the two selections cannot
     * end up describing different products.
     */
    components: z.array(z.string().max(64)).max(64),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateDeploymentModules = z.infer<typeof updateDeploymentModulesSchema>;

export const updateDeploymentThemeSchema = z
  .object({
    theme: themeSchema,
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateDeploymentTheme = z.infer<typeof updateDeploymentThemeSchema>;

/* -------------------------------------------------------------------------- */
/* Readiness                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The hand-over checklist.
 *
 * A deployment is not finished when the last form saves; it is finished when
 * somebody can sign in and use it. These are the things that are easy to leave
 * undone and expensive to discover afterwards -- a client whose first login
 * lands on an unnamed project, or a deployment activated with no account that
 * can administer it.
 *
 * Computed server-side from the actual rows, never stored. A stored checklist
 * is a second source of truth that goes stale the first time somebody deletes
 * the thing it was ticking off.
 */
export const DEPLOYMENT_CHECKS = [
  'branding',
  'project',
  'modules',
  'administrator',
  'supportContact',
] as const;
export const deploymentCheckKeySchema = z.enum(DEPLOYMENT_CHECKS);
export type DeploymentCheckKey = (typeof DEPLOYMENT_CHECKS)[number];

export const deploymentCheckSchema = z.object({
  key: deploymentCheckKeySchema,
  satisfied: z.boolean(),
  /** An unsatisfied advisory check does not block activation; a required one does. */
  required: z.boolean(),
  /** Interpolated into the translated hint, e.g. how many modules are on. */
  params: z.record(z.string(), z.unknown()).optional(),
});
export type DeploymentCheck = z.infer<typeof deploymentCheckSchema>;

export const deploymentReadinessSchema = z.object({
  stage: deploymentStageSchema,
  checks: z.array(deploymentCheckSchema),
  /** True when every *required* check passes, so activation is offered. */
  readyToActivate: z.boolean(),
});
export type DeploymentReadiness = z.infer<typeof deploymentReadinessSchema>;

/* -------------------------------------------------------------------------- */
/* The provider console's section catalog                                      */
/* -------------------------------------------------------------------------- */

/**
 * The console's sections, declared as data exactly like `SETTINGS_CATALOG`.
 *
 * Same extension model, deliberately a second catalog rather than a `platform`
 * category inside the first one. The settings page is the *client's*: its
 * sections are indexed by tenant permission, its navigation is rendered from a
 * list every signed-in administrator can enumerate, and adding a provider-only
 * category to it would mean one filtering mistake away from a client reading
 * the shape of the provider's control panel. Two catalogs, two pages, no
 * shared filter to get wrong.
 */
export interface PlatformSectionDefinition {
  id: string;
  titleKey: string;
  descriptionKey: string;
  requiredPermissions: readonly PlatformPermission[];
  order: number;
  /**
   * `singleton` (the default): the section edits the one `Deployment` record,
   * and its editor is handed it as a prop -- every section had this shape
   * until now, which is why it is the default rather than a value every entry
   * has to spell out.
   *
   * `list`: the section manages a collection of its own (email domains, Entra
   * tenant IDs, platform admins) and fetches its own data. It renders without
   * waiting on the deployment record, which it has no use for.
   */
  kind?: 'singleton' | 'list';
}

export const PLATFORM_SECTION_CATALOG = {
  overview: {
    id: 'overview',
    titleKey: 'platform.sections.overview.title',
    descriptionKey: 'platform.sections.overview.description',
    requiredPermissions: ['platform.deployment.read'],
    order: 10,
  },
  client: {
    id: 'client',
    titleKey: 'platform.sections.client.title',
    descriptionKey: 'platform.sections.client.description',
    requiredPermissions: ['platform.deployment.manage'],
    order: 20,
  },
  branding: {
    id: 'branding',
    titleKey: 'platform.sections.branding.title',
    descriptionKey: 'platform.sections.branding.description',
    requiredPermissions: ['platform.branding.manage'],
    order: 30,
  },
  modules: {
    id: 'modules',
    titleKey: 'platform.sections.modules.title',
    descriptionKey: 'platform.sections.modules.description',
    requiredPermissions: ['platform.modules.manage'],
    order: 40,
  },
  project: {
    id: 'project',
    titleKey: 'platform.sections.project.title',
    descriptionKey: 'platform.sections.project.description',
    requiredPermissions: ['platform.deployment.manage'],
    order: 50,
  },
  accessControl: {
    id: 'accessControl',
    titleKey: 'platform.sections.accessControl.title',
    descriptionKey: 'platform.sections.accessControl.description',
    requiredPermissions: ['platform.access_control.manage'],
    order: 60,
    kind: 'list',
  },
  /*
   * The background job queue, which used to be a client-facing page.
   *
   * A `list` section: it manages a collection of its own and has no use for the
   * deployment record, exactly like access control and admins.
   */
  operations: {
    id: 'operations',
    titleKey: 'platform.sections.operations.title',
    descriptionKey: 'platform.sections.operations.description',
    requiredPermissions: ['platform.operations.manage'],
    order: 65,
    kind: 'list',
  },
  admins: {
    id: 'admins',
    titleKey: 'platform.sections.admins.title',
    descriptionKey: 'platform.sections.admins.description',
    requiredPermissions: ['platform.admins.manage'],
    order: 70,
    kind: 'list',
  },
} as const satisfies Record<string, PlatformSectionDefinition>;

export type PlatformSectionId = keyof typeof PLATFORM_SECTION_CATALOG;

export const PLATFORM_SECTIONS: readonly PlatformSectionDefinition[] =
  Object.values(PLATFORM_SECTION_CATALOG);

export function listPlatformSections(
  permissions: readonly PlatformPermission[],
): PlatformSectionDefinition[] {
  const held = new Set<string>(permissions);
  return PLATFORM_SECTIONS.filter((section) =>
    section.requiredPermissions.every((permission) => held.has(permission)),
  ).sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** `hasOwn`, for the same reason `getSettingsSection` uses it: the id is a URL segment. */
export function getPlatformSection(id: string): PlatformSectionDefinition | undefined {
  if (!Object.hasOwn(PLATFORM_SECTION_CATALOG, id)) return undefined;
  return (PLATFORM_SECTION_CATALOG as Record<string, PlatformSectionDefinition>)[id];
}
